import { request } from "../api";
import { unwrapList } from "./devices";

export interface Employee {
  id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string;
  protection_active: boolean;
  last_entry_at: string | null;
  last_exit_at: string | null;
  last_event_at: string | null;
  last_event_source: string;
  device_count: number;
  /** Aggregate connectivity, independent of the badge-entry/presence state. */
  is_online?: boolean;
  online_device_count?: number;
  last_seen_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PresenceEvent {
  id: string;
  event_type: "entry" | "exit";
  source: string;
  occurred_at: string;
  received_at: string;
  request_ip: string | null;
  request_id: string;
  applied: boolean;
  notes: string;
  created_at: string;
}

interface Paginated<T> {
  count?: number;
  results?: T[];
}

export type AppAdoptionCategory =
  | "no_device"
  | "device_never_seen"
  | "device_stale"
  | "active";

export interface AppAdoptionSummary {
  days: number;
  stale_hours: number;
  total_badge_active: number;
  no_device: number;
  device_never_seen: number;
  device_stale: number;
  active: number;
  /** active / total_badge_active, 0.0 when total is 0. */
  adoption_rate: number;
}

export interface AppAdoptionRow {
  id: string;
  employee_id: string;
  full_name: string;
  phone: string;
  last_entry_at: string | null;
  last_event_at: string | null;
  /** Distinct calendar days with a presence event in the window. */
  badge_days_in_window: number;
  device_count: number;
  newest_device_last_seen_at: string | null;
  category: AppAdoptionCategory;
}

export interface AppAdoptionReport {
  summary: AppAdoptionSummary;
  /** Sorted worst-first: no_device -> device_never_seen -> device_stale -> active, then employee_id. */
  rows: AppAdoptionRow[];
}

function qs(params: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params) as [string, unknown][]) {
    if (v === undefined || v === "" || v === null) continue;
    sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const employeesApi = {
  list: async (params: { search?: string; protection_active?: boolean } = {}) => {
    const data = await request<Paginated<Employee> | Employee[]>(
      `/api/admin/v1/employees/${qs(params)}`,
    );
    return unwrapList(data);
  },
  get: (id: string) => request<Employee>(`/api/admin/v1/employees/${id}/`),
  create: (body: {
    employee_id: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
  }) =>
    request<Employee>(`/api/admin/v1/employees/`, { method: "POST", json: body }),
  patch: (id: string, body: Partial<{ first_name: string; last_name: string; phone: string; protection_active: boolean }>) =>
    request<Employee>(`/api/admin/v1/employees/${id}/`, { method: "PATCH", json: body }),
  forceEntry: (id: string) =>
    request<{ employee: Employee; affected_devices: number }>(
      `/api/admin/v1/employees/${id}/force-entry/`,
      { method: "POST" },
    ),
  forceExit: (id: string) =>
    request<{ employee: Employee; affected_devices: number }>(
      `/api/admin/v1/employees/${id}/force-exit/`,
      { method: "POST" },
    ),
  events: (id: string, limit = 50) =>
    request<PresenceEvent[]>(`/api/admin/v1/employees/${id}/events/?limit=${limit}`),
  /** "Badged in but not using the app" adoption report. Same service the `presence_adoption` scheduled-report type reuses. */
  appAdoption: (params: { days?: number; stale_hours?: number } = {}) =>
    request<AppAdoptionReport>(`/api/admin/v1/employees/app-adoption/${qs(params)}`),
};
